import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await fetch(`${API}/checkout/status/${sessionId}`);
        if (!response.ok) throw new Error("Failed to check status");

        const data = await response.json();
        setPaymentDetails(data);

        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment:", error);
        if (attempts >= maxAttempts - 1) {
          setStatus("error");
        } else {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  return (
    <div 
      className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4"
      data-testid="payment-success-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
      >
        {status === "loading" && (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="mx-auto mb-6"
            >
              <Loader2 size={64} className="text-[#C5A028]" />
            </motion.div>
            <h1 className="text-2xl font-serif text-stone-900 mb-4">
              Vérification du paiement...
            </h1>
            <p className="text-stone-600">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="mx-auto mb-6"
            >
              <CheckCircle2 size={64} className="text-green-500" />
            </motion.div>
            <h1 className="text-2xl font-serif text-stone-900 mb-4">
              Paiement réussi !
            </h1>
            <p className="text-stone-600 mb-6">
              Merci pour votre commande. Nous avons bien reçu votre paiement pour le{" "}
              <strong>{paymentDetails?.metadata?.package_name || "pack choisi"}</strong>.
            </p>
            {paymentDetails && (
              <div className="bg-[#FDFBF7] rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-stone-600">
                  <span className="font-medium">Montant :</span>{" "}
                  {paymentDetails.amount_total} {paymentDetails.currency?.toUpperCase()}
                </p>
              </div>
            )}
            <p className="text-sm text-stone-500 mb-6">
              Vous recevrez un email de confirmation avec les prochaines étapes pour créer votre invitation personnalisée.
            </p>
            <Link to="/">
              <Button className="bg-[#0A0A0A] hover:bg-stone-800 text-white rounded-full px-8">
                <ArrowLeft size={16} className="mr-2" />
                Retour à l'accueil
              </Button>
            </Link>
          </>
        )}

        {(status === "error" || status === "expired" || status === "timeout") && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="mx-auto mb-6"
            >
              <XCircle size={64} className="text-red-500" />
            </motion.div>
            <h1 className="text-2xl font-serif text-stone-900 mb-4">
              {status === "expired" ? "Session expirée" : "Erreur de paiement"}
            </h1>
            <p className="text-stone-600 mb-6">
              {status === "expired" 
                ? "Votre session de paiement a expiré. Veuillez réessayer."
                : "Une erreur est survenue lors de la vérification de votre paiement."}
            </p>
            <Link to="/#nos-offres">
              <Button className="bg-[#0A0A0A] hover:bg-stone-800 text-white rounded-full px-8">
                <ArrowLeft size={16} className="mr-2" />
                Réessayer
              </Button>
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
